/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { uniq } from 'lodash'
import { useIntl } from 'react-intl'

import type { EuiBasicTableColumn } from '@elastic/eui'
import {
  EuiFilterGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiInMemoryTable,
  EuiSpacer,
} from '@elastic/eui'

import { useListTrafficFilterRulesetsQuery } from '@modules/cloud-lib/networkSecurity/hooks'
import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import type { RulesetType } from '@modules/network-security-lib/types'

import TrafficFilterForm from '../TrafficFilterForm'

import {
  defaultColumn,
  makeActionsColumn,
  nameColumn,
  providerColumn,
  regionColumn,
  resourcesColumn,
  typeColumn,
} from './columns'
import SearchBar from './SearchBar'
import CreateButton from './CreateButton'
import RegionFilter from './filters/RegionFilter'

const PAGINATE_FLOOR = 20 // the number of displayable rows before the table will paginate

const TrafficFilterList: React.FunctionComponent = () => {
  const { formatMessage } = useIntl()
  const [params] = useSearchParams()

  const search = (params.get('search') ?? '').trim().toLowerCase()
  const selectedRegionIds = (params.get('region') || null)?.split(',') || []
  const { data, isLoading } = useListTrafficFilterRulesetsQuery()

  const [currentRuleset, setCurrentRuleset] = useState<TrafficFilterRulesetInfo | null>(null)
  const [creatingRulesetType, setCreatingRulesetType] = useState<RulesetType | null>(null)

  const columns: Array<EuiBasicTableColumn<TrafficFilterRulesetInfo> | any> = [
    nameColumn,
    typeColumn,
    defaultColumn,
    resourcesColumn,
    providerColumn,
    regionColumn,
    makeActionsColumn({
      formatMessage,
      onEdit: (ruleset) => {
        setCurrentRuleset(ruleset)
      },
    }),
  ]

  const regionIds = uniq((data?.rulesets ?? []).map((item) => item.region))

  const filteredData = (data?.rulesets ?? [])
    .filter(({ name }) => !search || name.toLowerCase().includes(search))
    .filter(({ region }) => !selectedRegionIds.length || selectedRegionIds.includes(region))

  const sorting = {
    sort: {
      field: 'name',
      direction: 'asc' as const,
    },
  }

  return (
    <Fragment>
      <EuiFlexGroup>
        <EuiFlexItem grow={true}>
          <SearchBar />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFilterGroup>
            <RegionFilter regionIds={regionIds} />
          </EuiFilterGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <CreateButton
            onClick={(rulesetType) => {
              setCreatingRulesetType(rulesetType)
            }}
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer />

      <EuiInMemoryTable
        tableLayout='auto'
        items={filteredData}
        columns={columns}
        sorting={sorting}
        loading={isLoading}
        pagination={filteredData.length > PAGINATE_FLOOR}
      />

      {renderFlyout()}
    </Fragment>
  )

  function renderFlyout() {
    const onClose = () => {
      setCreatingRulesetType(null)
      setCurrentRuleset(null)
    }

    if (creatingRulesetType) {
      return <TrafficFilterForm onClose={onClose} newRulesetType={creatingRulesetType} />
    }

    if (currentRuleset) {
      return <TrafficFilterForm onClose={onClose} ruleset={currentRuleset} />
    }

    return null
  }
}

export default TrafficFilterList
